<script lang="ts">
  import { createEventDispatcher } from "svelte";
  import { onDestroy, onMount } from "svelte";
  import type { Banner } from "$lib/gql/genCms/codegen";
  import Container from "../Container/Container.svelte";
  import Icon from "../Icon/Icon.svelte";
  import Typography from "../Typography/Typography.svelte";
  import { formatCountdownBannerText } from "$lib/utils/countdown";
  import { isInternalLink, linkTo, processInlineLinks } from "$lib/utils/url";
  export let bannerData: Banner;
  import BannerLinkWrapper from "./BannerLinkWrapper/BannerLinkWrapper.svelte";

  function targetLink(href: string): string {
    // check for external link
    if (!isInternalLink(href)) {
      return "_blank";
    }
    return "_self";
  }

  const dispatch = createEventDispatcher();
  let countdown = false,
    bannerText = "",
    finished = false;

  if (bannerData.text && bannerData.text.html !== "") {
    ({ countdown, bannerText, finished } = formatCountdownBannerText(
      bannerData.text.html,
    ));
  }

  let timer = null;

  onMount(() => {
    if (countdown && !finished && bannerData.text) {
      timer = setInterval(() => {
        bannerText = formatCountdownBannerText(bannerData.text.html).bannerText;
      }, 1000);
    }
  });

  onDestroy(() => {
    if (timer) {
      clearInterval(timer);
    }
  });

  // On close, add banner to localStorage so it can be ignored next time
  function closeBanner() {
    dispatch("closeBanner");
  }
</script>

<style lang="scss">
  .banner {
    background-color: $surface;
    height: 100%;

    > :global(div) {
      height: 100%;
    }

    &--content {
      padding: 8px;
      display: flex;
      height: 100%;
      align-items: center;
      justify-content: center;
      position: relative;

      @include breakpoint(md) {
        padding: 12px 0;
      }
    }

    &--message {
      display: flex;
      flex-direction: column;
      text-align: center;

      @include breakpoint(md) {
        align-items: center;
        padding: 0 24px;
      }

      @include breakpoint(lg) {
        flex-direction: row;
      }
    }

    &--message-wrapper {
      display: flex;
      align-items: flex-start;
      justify-content: center;

      @include breakpoint(md) {
        align-items: center;
      }
    }
    &--icon {
      font-size: 0;
      margin-right: 5px;
    }

    &--close {
      background: none;
      border: none;
      padding: 0;
      margin: 0;
      display: block;
      position: absolute;
      right: 16px;
      top: 50%;
      transform: translateY(-50%) translateX(-50%);
      z-index: 1000;

      @include breakpoint(md) {
        right: 66px;
      }
      @include breakpoint(xl) {
        right: 100px;
      }
    }
  }

  .links {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 0.5rem !important;

    @include breakpoint(md) {
      margin-top: 0.5rem !important;
    }
    @include breakpoint(lg) {
      margin-top: 0 !important;
    }
    &--pipe {
      margin: 0 10px;
    }
    :global(a) {
      &:hover {
        text-decoration: none;
      }
    }
    a {
      text-decoration: none;
    }
    &--default {
      :global(a) {
        &:first-child {
          margin-left: 0;
        }

        @include breakpoint(lg) {
          &:first-child {
            margin-left: 24px;
          }
        }
      }
    }

    &--with-text {
      &:first-child {
        margin-left: 0;
      }
    }
  }

  .html :global(p),
  .html :global(span) {
    padding: 0;
    margin: 0;
  }
</style>

{#if bannerData.text || bannerData.link1.text !== ""}
  <div
    class="banner"
    id={bannerData.id}
    style="background-color: {bannerData.bgColour}"
  >
    <BannerLinkWrapper {bannerData}>
      <div class="banner--content">
        <Container>
          <div class={bannerData.text ? "banner--message" : "banner--links"}>
            <div class="banner--message-wrapper">
              {#if bannerData.icon}
                <span class="banner--icon">
                  <Icon
                    icon={bannerData.icon}
                    color={bannerData.textLight
                      ? "var(--inverse-on-surface)"
                      : "var(--on-surface)"}
                  />
                </span>
              {/if}
              {#if bannerData.text && bannerData.text.html !== ""}
                <Typography
                  variant="caption2"
                  component="span"
                  theme={bannerData.textLight
                    ? "inverse-on-surface"
                    : "on-surface"}
                >
                  <div class="html">
                    {@html processInlineLinks(bannerText)}
                  </div>
                </Typography>
              {/if}
            </div>

            {#if bannerData.link1 || bannerData.link2}
              <div
                class={bannerData.text
                  ? "links links--default"
                  : "links links--with-text"}
              >
                <Typography
                  variant="button1"
                  component="span"
                  theme={bannerData.textLight
                    ? "inverse-on-surface"
                    : "on-surface"}
                >
                  {#if bannerData.link1 && bannerData.link1.href}
                    <a
                      href={linkTo(bannerData.link1.href)}
                      target={targetLink(bannerData.link1.href)}
                    >
                      {bannerData.link1.text}
                    </a>
                  {/if}

                  {#if bannerData.link2 && bannerData.link2.href}
                    <span class="links--pipe">
                      <Typography
                        component="span"
                        variant="button1"
                        theme={bannerData.textLight
                          ? "inverse-on-surface-30"
                          : "on-surface-30"}>|</Typography
                      >
                    </span>
                    <a
                      href={linkTo(bannerData.link2.href)}
                      target={targetLink(bannerData.link2.href)}
                    >
                      {bannerData.link2.text}
                    </a>
                  {/if}
                </Typography>
              </div>
            {/if}
          </div>
        </Container>
      </div>
    </BannerLinkWrapper>

    <button type="button" class="banner--close" on:click={closeBanner}>
      <Icon
        icon="close"
        color={bannerData.textLight
          ? "var(--inverse-on-surface)"
          : "var(--on-surface)"}
      />
    </button>
  </div>
{/if}
